/** Wire payload -> the domain shape ItineraryDetailsScreenStateMapper is written for. */
import { toDomainImage, type DomainImage } from '../images';

export interface ItineraryImage {
  id: number;
  image: DomainImage | null;
  videoUrl: string | null;
  sequence: number;
  hasVideo: boolean;
}

export interface ItineraryDay {
  id: number;
  sequence: number;
  daySpan: number;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  footer: string | null;
  image: DomainImage | null;
}

export interface ItineraryDomain {
  id: number;
  name: string;
  description: string | null;
  highlights: string | null;
  footer: string | null;
  months: number[];
  routeMapImage: DomainImage | null;
  coverImage: DomainImage | null;
  images: ItineraryImage[];
  days: ItineraryDay[];
  customDayTitles: boolean;
  cost: number | null;
  costPrefix: string | null;
  costUnit: string | null;
  ctaButtonText: string | null;
  shareText: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

export function adaptItinerary(payload: Record<string, unknown>): ItineraryDomain {
  const rawImages = Array.isArray(payload.images) ? payload.images : [];
  const rawDays = Array.isArray(payload.days) ? payload.days : [];

  return {
    id: Number(payload.id ?? 0),
    name: str(payload.name) ?? '',
    description: str(payload.description),
    highlights: str(payload.highlights),
    footer: str(payload.footer),
    months: Array.isArray(payload.months) ? (payload.months as number[]) : [],
    routeMapImage: toDomainImage(payload.route_map),
    coverImage: toDomainImage(payload.cover_image),
    images: rawImages.map((r) => {
      const o = r as Record<string, unknown>;
      const videoUrl = str(o.video_url);
      return {
        id: Number(o.id ?? 0),
        image: toDomainImage(o.image),
        videoUrl,
        sequence: num(o.sequence) ?? 0,
        hasVideo: videoUrl !== null,
      };
    }),
    days: rawDays.map((r) => {
      const o = r as Record<string, unknown>;
      return {
        id: Number(o.id ?? 0),
        sequence: num(o.sequence) ?? 0,
        daySpan: num(o.day_span) ?? 1,
        title: str(o.title),
        subtitle: str(o.subtitle),
        description: str(o.description),
        footer: str(o.footer),
        image: toDomainImage(o.image),
      };
    }),
    customDayTitles: payload.custom_day_titles === true,
    cost: num(payload.cost),
    costPrefix: str(payload.cost_prefix),
    costUnit: str(payload.cost_unit),
    ctaButtonText: str(payload.cta_button_text),
    shareText: str(payload.branchio_link),
  };
}

/** `Itinerary.getDaysRange(index)` — walks day_span from day 1.
 *  Returns [start, end] with end === null when the day spans a single day. */
export function getDaysRange(days: ItineraryDay[], index: number): [number, number | null] {
  let start = 1;
  for (let i = 0; i < index; i += 1) start += days[i]?.daySpan ?? 1;
  const span = days[index]?.daySpan ?? 1;
  return span > 1 ? [start, start + span - 1] : [start, null];
}
